import React from 'react';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@/components/ui/sheet';
import * as d3 from 'd3';
import { useEffect, useRef, useState, useMemo } from 'react';
import { FilterDropdowns } from './fillter-dropdown';
import { useGetMapCategories, useGetCategoryMapData } from '@/services/mapdata/queries';
import { Button } from '@/components/ui/button';
import { SlidersHorizontal, ZoomIn, ZoomOut } from 'lucide-react';
import SidebarFilter from './sidebar-filter';
import _ from 'lodash';
import OrganisationDetails from './organisation-details';
import { ResetIcon } from '@radix-ui/react-icons';
import { isStringUpperCase, cn } from '@/lib/utils';
import type { MapCategory, MapData } from '@/services/types';
import { useAuthStore } from '@/store/auth';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
 

interface Node {
  id: string;
  name: string;
  type: 'organisation' | 'category';
  noOfLinks: number;
  x?: number;
  y?: number;
}

interface Link {
  source: string;
  target: string;
}

interface GraphData {
  nodes: Node[];
  links: Link[];
  isAllCategoriesUnselected: boolean;
}

function buildGraph(
  mapData: MapData[],
  uncheckedOrganisations: Set<string>,
  uncheckedCategories1: Set<string>,
  uncheckedCategories2: Set<string>,
  selectedCategory1Id: string,
  selectedCategory2Id: string,
  categories: MapCategory[],
  filterLogic: 'AND' | 'OR' = 'OR'
): GraphData {
  const nodes: Node[] = [];
  const links: Link[] = [];
  const availableCategories1 = new Set<string>();
  const availableCategories2 = new Set<string>();

  // Find the selected categories
  const selectedCategory1 = categories.find(cat => cat.id === selectedCategory1Id);
  const selectedCategory2 = categories.find(cat => cat.id === selectedCategory2Id);
  
  // Need at least one category selected
  if (!selectedCategory1 && !selectedCategory2) {
    return { nodes: [], links: [], isAllCategoriesUnselected: true };
  }

  // Collect all available tags for both categories
  mapData.forEach((dataItem) => {
    if (selectedCategory1 && dataItem.category === selectedCategory1Id) {
      dataItem.tags.forEach(tag => availableCategories1.add(tag));
    }
    if (selectedCategory2 && dataItem.category === selectedCategory2Id) {
      dataItem.tags.forEach(tag => availableCategories2.add(tag));
    }
  });

  // Check if all categories are unselected for both
  const isAllCategories1Unselected = 
    selectedCategory1 && uncheckedCategories1.size === availableCategories1.size && availableCategories1.size > 0;
  const isAllCategories2Unselected = 
    selectedCategory2 && uncheckedCategories2.size === availableCategories2.size && availableCategories2.size > 0;
  
  if (isAllCategories1Unselected && isAllCategories2Unselected) {
    return { nodes: [], links: [], isAllCategoriesUnselected: true };
  }

  // Group map data by organisation for both categories
  const organisationMap = new Map<string, { category1: MapData[], category2: MapData[] }>();
  
  mapData.forEach((dataItem) => {
    if (!dataItem.organisation) return;
    
    const orgId = typeof dataItem.organisation === 'string' 
      ? dataItem.organisation 
      : dataItem.organisation?.id;
    
    const isCategory1 = selectedCategory1 && dataItem.category === selectedCategory1Id;
    const isCategory2 = selectedCategory2 && dataItem.category === selectedCategory2Id;
    
    if (!isCategory1 && !isCategory2) return;
    
    if (!organisationMap.has(orgId)) {
      organisationMap.set(orgId, { category1: [], category2: [] });
    }
    
    const orgData = organisationMap.get(orgId)!;
    if (isCategory1) orgData.category1.push(dataItem);
    if (isCategory2) orgData.category2.push(dataItem);
  });

  // Build nodes and links for both categories
  organisationMap.forEach((orgData, orgId) => {
    // Get organisation name safely
    const org1 = orgData.category1[0]?.organisation;
    const org2 = orgData.category2[0]?.organisation;
    const orgName = typeof org1 === 'string'
      ? orgId
      : typeof org1 === 'object' && org1?.name
      ? org1.name
      : typeof org2 === 'object' && org2?.name
      ? org2.name
      : orgId;
    
    const isOrganisationFiltered = uncheckedOrganisations.has(orgName);
    
    // Get tags from both categories
    const orgTags1 = orgData.category1.flatMap(data => data.tags);
    const orgTags2 = orgData.category2.flatMap(data => data.tags);
    const allOrgTags = [...orgTags1, ...orgTags2];
    
    // Check if any tags from category 1 are selected
    const hasSelectedTag1 = orgTags1.length > 0 && orgTags1.some(tag => !uncheckedCategories1.has(tag));
    // Check if any tags from category 2 are selected
    const hasSelectedTag2 = orgTags2.length > 0 && orgTags2.some(tag => !uncheckedCategories2.has(tag));
    
    // Determine if organization should be shown based on AND/OR logic
    // If both categories are selected, use the filterLogic (AND/OR)
    // If only one category is selected, always show if that category has selected tags
    let hasAnySelectedTag: boolean;
    if (selectedCategory2Id && selectedCategory1Id) {
      // Both categories selected - use filterLogic
      hasAnySelectedTag = filterLogic === 'AND' 
        ? hasSelectedTag1 && hasSelectedTag2  // AND logic
        : hasSelectedTag1 || hasSelectedTag2;  // OR logic
    } else {
      // Only one category selected - use OR logic
      hasAnySelectedTag = hasSelectedTag1 || hasSelectedTag2;
    }

    // Organisation node
    const organisationNode: Node = {
      id: orgId,
      name: orgName,
      type: 'organisation',
      noOfLinks: allOrgTags.length,
    };

    // Only process organization and its tags if it passes the filter
    if (!isOrganisationFiltered && hasAnySelectedTag) {
      nodes.push(organisationNode);

      // Tag nodes and links for category 1 (pink)
      orgTags1.forEach((tag) => {
        if (uncheckedCategories1.has(tag)) return;

        const tagNodeId = `tag1_${tag.trim().toLowerCase().replace(/\s/g, '_')}`;
        
        let tagNode = nodes.find((node) => node.id === tagNodeId);

        if (tagNode) {
          tagNode.noOfLinks += 1;
        } else {
          tagNode = {
            id: tagNodeId,
            name: tag,
            type: 'category',
            noOfLinks: 1,
          };
          nodes.push(tagNode);
        }

        const linkExists = links.some(
          (link) => link.source === orgId && link.target === tagNodeId
        );

        if (!linkExists) {
          links.push({ source: orgId, target: tagNodeId });
        }
      });

      // Tag nodes and links for category 2 (amber)
      orgTags2.forEach((tag) => {
        if (uncheckedCategories2.has(tag)) return;

        const tagNodeId = `tag2_${tag.trim().toLowerCase().replace(/\s/g, '_')}`;
        
        let tagNode = nodes.find((node) => node.id === tagNodeId);

        if (tagNode) {
          tagNode.noOfLinks += 1;
        } else {
          tagNode = {
            id: tagNodeId,
            name: tag,
            type: 'category',
            noOfLinks: 1,
          };
          nodes.push(tagNode);
        }

        const linkExists = links.some(
          (link) => link.source === orgId && link.target === tagNodeId
        );

        if (!linkExists) {
          links.push({ source: orgId, target: tagNodeId });
        }
      });
    }
  });

  // Filter links to ensure all source and target nodes exist in the nodes array
  // This prevents D3 errors when switching between AND/OR logic
  const nodeIds = new Set(nodes.map(node => node.id));
  const validLinks = links.filter(link => {
    const sourceId = typeof link.source === 'string' ? link.source : (link.source as any).id;
    const targetId = typeof link.target === 'string' ? link.target : (link.target as any).id;
    return nodeIds.has(sourceId) && nodeIds.has(targetId);
  });

  return { nodes, links: validLinks, isAllCategoriesUnselected: false };
}

const StakeHolderMap = () => {
  const ZoomRef = useRef<d3.ZoomBehavior<Element, unknown>>(null);
  
  const [isShowFilter, setIsShowFilter] = useState(false);
  const [isShowOrganisationDetails, setIsShowOrganisationDetails] = useState(false);
  const [selectedOrganisation, setSelectedOrganisation] = useState<string | null>(null);
  const [uncheckedCategories1, setUncheckedCategories1] = useState<Set<string>>(new Set());
  const [uncheckedCategories2, setUncheckedCategories2] = useState<Set<string>>(new Set());
  const [uncheckedOrganisations, setUncheckedOrganisations] = useState<Set<string>>(new Set());
  const [selectedCategory1, setSelectedCategory1] = useState<{id: string, name: string}>({
    id: '',
    name: ''
  });
  const [selectedCategory2, setSelectedCategory2] = useState<{id: string, name: string}>({
    id: '',
    name: ''
  });
  const [filterLogic, setFilterLogic] = useState<'AND' | 'OR'>('OR');
  
  // Reset unchecked categories when categories change
  useEffect(() => {
    setUncheckedCategories1(new Set());
  }, [selectedCategory1.id]);

  useEffect(() => {
    setUncheckedCategories2(new Set());
  }, [selectedCategory2.id]);
  
  // Debounced filter changes
  const handleUncheckedCategories1 = _.debounce((unchecked: Set<string>) => {
    setUncheckedCategories1(unchecked);
  }, 500);

  const handleUncheckedCategories2 = _.debounce((unchecked: Set<string>) => {
    setUncheckedCategories2(unchecked);
  }, 500);

  const handleUncheckedOrganisations = _.debounce((unchecked: Set<string>) => {
    setUncheckedOrganisations(unchecked);
  }, 500);

  const handleFilterSheetChange = (open: boolean) => {
    setIsShowFilter(open);
  };

  const activeWorkspace = useAuthStore((state) => state.activeWorkspace);
  const workspaceId = activeWorkspace || '';
  
  const { data: mapCategories = {data: [], pagination: {}}, isLoading: isLoadingCategories } = useGetMapCategories(workspaceId, undefined, {
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5
  });
  
  // Fetch data for both categories
  const { data: mapData1 = [], isLoading: isLoadingMapData1 } = useGetCategoryMapData(
    selectedCategory1.id,
    { enabled: !!selectedCategory1.id,
      staleTime: 1000 * 60 * 2,
      gcTime: 1000 * 60 * 2
    }
  );

  const { data: mapData2 = [], isLoading: isLoadingMapData2 } = useGetCategoryMapData(
    selectedCategory2.id,
    { enabled: !!selectedCategory2.id,
      staleTime: 1000 * 60 * 2,
      gcTime: 1000 * 60 * 2
    }
  );

  // Combine map data from both categories
  const mapData = useMemo(() => {
    return [...mapData1, ...mapData2];
  }, [mapData1, mapData2]);

  const isLoadingMapData = isLoadingMapData1 || isLoadingMapData2;

  const graphData = useMemo(() => {
    if ((!selectedCategory1.id && !selectedCategory2.id) || mapCategories.data.length === 0) {
      return { nodes: [], links: [], isAllCategoriesUnselected: false };
    }
    
    return buildGraph(
      mapData,
      uncheckedOrganisations,
      uncheckedCategories1,
      uncheckedCategories2,
      selectedCategory1.id,
      selectedCategory2.id,
      mapCategories.data,
      filterLogic
    );
  }, [mapData, uncheckedOrganisations, uncheckedCategories1, uncheckedCategories2, selectedCategory1, selectedCategory2, mapCategories, filterLogic]);

  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const initialScale = 0.07; // Adjust this value to zoom out more or less

  const width = containerRef.current?.clientWidth ?? window.innerWidth;
  const height = containerRef.current?.clientHeight ?? window.innerHeight;
  const baseRadius = 20;

  // State for zoom interval
  const [zoomInterval, setZoomInterval] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    d3.select('.tooltip').style('z-index', -1000);
    d3.select(svgRef.current).selectAll('*').remove();

    if (!graphData.nodes.length || isLoadingMapData) return;

    const { nodes, links } = graphData;

    const zoom = d3.zoom().scaleExtent([0.09, 4]).on('zoom', zoomed);
    ZoomRef.current = zoom;

    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .style('background', '#fdfdfd')
      .call(zoom as any)
      .on('click', resetHighlight)
      .append('g');

    d3.select(svgRef.current)
      .transition()
      .duration(500)
      .call(
        zoom.transform as any,
        d3.zoomIdentity.translate(width / 2, height / 2).scale(initialScale)
      );

    const simulation = d3
      .forceSimulation(nodes)
      .force(
        'link',
        d3
          .forceLink(links)
          .id((d: any) => d.id)
          .distance(2000)
      )
      .force('charge', d3.forceManyBody().strength(-1000))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('x', d3.forceX())
      .force('y', d3.forceY())
      .force(
        'collide',
        d3.forceCollide((d: Node) => d.noOfLinks * 3 + 300).iterations(29) 
      )
      .on('tick', ticked);

    simulation.alphaDecay(0.08);

    const link = svg
      .append('g')
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.9)
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke-width', 3);

    const nodeGroup = svg
      .append('g')
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'node-group')
      .on('click', nodeClicked)
      .on('mouseover', showTooltip)
      .on('mouseout', hideTooltip)
      .on('contextmenu', (event, d) =>
        d.type === 'organisation'
          ? handleNodeClick(event, d)
          : event.preventDefault()
      )
      .on('dblclick', (event, d) =>
        d.type === 'organisation'
          ? handleNodeClick(event, d)
          : event.preventDefault()
      )
      .call(
        d3
          .drag()
          .on('start', dragstarted as any)
          .on('drag', dragged as any)
          .on('end', dragended as any) as any
      );

    // Add circles for nodes
    const node = nodeGroup
      .append('circle')
      .attr('r', (d) => Math.max(100, (d.noOfLinks * 2 + 200) / 2))
      .attr('fill', (d) => {
        if (d.type === 'organisation') return '#60a5fa';
        // Category 1 tags (pink) start with tag1_, Category 2 tags (amber) start with tag2_
        if (d.id.startsWith('tag1_')) return '#f472b6'; // Pink for category 1
        if (d.id.startsWith('tag2_')) return '#f59e0b'; // Amber/Orange for category 2
        return '#f472b6'; // Default to pink
      })
      .attr('stroke', '#fff')
      .attr('stroke-width', '1.5px')
      .style('cursor', 'grab');

      nodeGroup
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle') // vertical centering
      .attr('font-size', '28px')
      .attr('font-weight', '500')
      .attr('fill', '#000')
      .attr('pointer-events', 'none')
      .style('text-transform', 'capitalize')
      .text((d) => {
        const displayName =
          d.name.length > 20 ? d.name.slice(0, 20) + '...' : d.name;
        return isStringUpperCase(d.name) ? displayName : displayName.toLowerCase();
      })
      .each(function () {
        const text = d3.select(this);
        const parentCircle = d3.select(this.parentNode as Element).select('circle');
        const radius = +parentCircle.attr('r');
        const maxTextWidth = radius * 1.6;
    
        const node = this;
        while (node.getComputedTextLength() > maxTextWidth && parseFloat(text.attr('font-size')) > 8) {
          text.attr('font-size', parseFloat(text.attr('font-size')) - 1);
        }
      });

    var tooltip = d3
      .select('body')
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background-color', 'white')
      .style('border', 'solid')
      .style('border-width', '1px')
      .style('border-radius', '5px')
      .style('padding', '5px')
      .style('box-shadow', '0px 0px 10px rgba(0,0,0,0.1)')
      .style('z-index', -1000);

    function zoomed(event: d3.D3ZoomEvent<SVGSVGElement, unknown>) {
      svg.attr('transform', event.transform as any);
    }

    function ticked() {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      nodeGroup
        .attr('transform', (d: any) => `translate(${d.x ?? 0}, ${d.y ?? 0})`);
    }

    function nodeClicked(this: any, event: any, d: Node) {
      resetHighlight();

      // Dim all nodes and links
      node.attr('opacity', 0.1);
      link.style('stroke-opacity', 0.1);

      // Highlight the clicked node
      const clickedGroup = d3.select(this as any);
      clickedGroup
        .select('circle')
        .attr('stroke', '#000')
        .attr('stroke-width', '3px')
        .attr('opacity', 1);

      clickedGroup
        .select('text')
        .attr('font-weight', 'bold')

      // Highlight connected links
      link
        .filter((l: any) => l.source.id === d.id || l.target.id === d.id)
        .style('stroke-opacity', 1)
        .style('stroke-width', 2)
        .style('stroke', 'black');

      // Highlight connected nodes
      nodeGroup
        .filter((n: Node) => isConnected(n, d))
        .select('circle')
        .attr('opacity', 1);

      // Highlight nodes with the same name
      nodeGroup
        .filter((n) => n.name === d.name)
        .select('circle')
        .attr('opacity', 1);

      event.stopPropagation();
    }

    function handleNodeClick(event: React.MouseEvent, d: Node) {
      event.preventDefault();
      setSelectedOrganisation(d.id);
      setIsShowOrganisationDetails(true);
    }

    function resetHighlight() {
      // Reset all nodes to full opacity and default stroke
      node
        .attr('opacity', 1)
        .attr('stroke', '#fff')
        .attr('stroke-width', '1.5px');

      // Reset all links to default appearance
      link
        .style('stroke-opacity', 0.9)
        .style('stroke', '#999')
        .style('stroke-width', 3);
      
      // Reset all text to default appearance
      nodeGroup.select('text')
        .attr('font-weight', 'normal')
        .attr('fill', '#000');
      
      hideTooltip();
    }

    function isConnected(a: Node, b: any) {
      return links.some(
        (link: any) =>
          (link.source.id === a.id && link.target.id === b.id) ||
          (link.source.id === b.id && link.target.id === a.id)
      );
    }

    function showTooltip(event: React.MouseEvent, d: Node) {
      let categoryName = 'Category';
      if (d.type === 'category') {
        // Determine which category based on node id prefix
        if (d.id.startsWith('tag1_')) {
          categoryName = selectedCategory1?.name || 'Category 1';
        } else if (d.id.startsWith('tag2_')) {
          categoryName = selectedCategory2?.name || 'Category 2';
        }
      }
      
      tooltip
        .transition()
        .duration(200)
        .style('opacity', 0.9)
        .style('z-index', 1000);
      tooltip
        .html(
          `${d.name} <br><small class='${isStringUpperCase(d.name) ? '' : 'capitalize'}'>(${d.type === 'organisation' ? 'Organization' : categoryName}) ${d.type === 'organisation' ? '<br/> Right click to view details' : ''}</small>`
        )
        .style('left', event.pageX + 10 + 'px')
        .style('top', event.pageY - 28 + 'px');
    }

    function hideTooltip() {
      tooltip
        .transition()
        .duration(200)
        .style('opacity', 0)
        .style('z-index', -1000);
    }

    function dragstarted(
      event: d3.D3DragEvent<SVGSVGElement, unknown, unknown>,
      d: Node & { fx?: number; fy?: number }
    ) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(
      event: d3.D3DragEvent<SVGSVGElement, unknown, unknown>,
      d: Node & { fx?: number | null; fy?: number | null }
    ) {
      d.fx = Math.max(baseRadius, Math.min(width - baseRadius, event.x));
      d.fy = Math.max(baseRadius, Math.min(height - baseRadius, event.y));
    }

    function dragended(
      event: d3.D3DragEvent<SVGSVGElement, unknown, unknown>,
      d: Node & { fx?: number | null; fy?: number | null }
    ) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    // const observer = new ResizeObserver(() => {
    //   const width = containerRef.current?.clientWidth ?? window.innerWidth;
    //   const height = containerRef.current?.clientHeight ?? window.innerHeight;
    //   svg.attr('width', width).attr('height', height);
    //   simulation.force('center', d3.forceCenter(width / 2, height / 2));
    //   simulation.alpha(0.3).restart();
    // });
    // if (containerRef.current) observer.observe(containerRef.current);
    return () => {
      // observer.disconnect();
      d3.select(svgRef.current).selectAll('*').remove();
    };
  }, [
    graphData,
    mapCategories,
    selectedCategory1,
    selectedCategory2,
    width,
    height,
    isLoadingMapData
  ]);

  const handleZoomIn = () => {
    if (!ZoomRef.current || !svgRef.current) return;
    d3.select(svgRef.current)
      .transition()
      .duration(100)
      .call(ZoomRef.current?.scaleBy as any, 1.2);
  };

  const handleZoomOut = () => {
    if (!ZoomRef.current || !svgRef.current) return;
    d3.select(svgRef.current)
      .transition()
      .duration(100)
      .call(ZoomRef.current?.scaleBy as any, 0.8);
  };

  const handleResetZoom = () => {
    if (!ZoomRef.current || !svgRef.current) return;
    d3.select(svgRef.current)
      .transition()
      .duration(400)
      .call(
        ZoomRef.current?.transform as any,
        d3.zoomIdentity.translate(width / 2, height / 2).scale(initialScale)
      );
  };

  const startZoomIn = () => {
    if (zoomInterval) return;
    const interval = setInterval(() => {
      handleZoomIn();
    }, 50);
    setZoomInterval(interval);
  };

  const stopZoomIn = () => {
    if (zoomInterval) {
      clearInterval(zoomInterval);
      setZoomInterval(null);
    }
  };

  const startZoomOut = () => {
    if (zoomInterval) return;
    const interval = setInterval(() => {
      handleZoomOut();
    }, 50);
    setZoomInterval(interval);
  };

  const stopZoomOut = () => {
    if (zoomInterval) {
      clearInterval(zoomInterval);
      setZoomInterval(null);
    }
  };

  return (
    <div className='flex h-full w-full flex-col'>
      <div className='relative flex h-full w-full flex-1' ref={containerRef}>
        <div className='absolute -top-2 left-2 flex w-full justify-between bg-transparent'>
          <FilterDropdowns 
            categories={mapCategories.data}
            selectedCategory1={selectedCategory1}
            selectedCategory2={selectedCategory2}
            onCategory1Change={setSelectedCategory1}
            onCategory2Change={setSelectedCategory2}
            isLoading={isLoadingCategories}
          />
          <div className='mr-10 mt-5 flex h-fit gap-2 rounded-md bg-white p-2'>
            <Button
              className={`aspect-video bg-transparent p-1 text-black ${isShowOrganisationDetails ? 'bg-blue-60 text-white' : ''}`}
              title='Filter'
              id='map-filter'
              disabled={
                isShowOrganisationDetails || (!selectedCategory1.id && !selectedCategory2.id)
              }
              onClick={() => setIsShowFilter(true)}
            >
              <SlidersHorizontal className='h-5 w-5' />
              <span className='ml-1'>Filter</span>
            </Button>
          </div>
        </div>

        {mapData && (selectedCategory1.id || selectedCategory2.id) ? (
          <>
            {isLoadingMapData ? (
              <div className='grid h-[calc(100vh-10rem)] w-full place-items-center'>
                <div className='text-gray-500'>Loading...</div>
              </div>
            ) : (
              <div className='flex-1'>
                {graphData.isAllCategoriesUnselected ? (
                  <div className='grid h-[calc(100vh-10rem)] w-full place-items-center'>
                    <div className='text-center'>
                      <p className='text-gray-500 text-lg'>Please select at least one category on the filter</p>
                    </div>
                  </div>
                ) : graphData.nodes.filter(node => node.type === 'organisation').length === 0 ? (
                  <div className='grid h-[calc(100vh-10rem)] w-full place-items-center'>
                    <div className='text-center max-w-md px-4'>
                      <div className='mb-4'>
                        <div className='mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4'>
                          <SlidersHorizontal className='h-8 w-8 text-gray-400' />
                        </div>
                      </div>
                      <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                        No organisations match your filters
                      </h3>
                      <p className='text-gray-500 text-sm'>
                        Try adjusting your filter selections to see more results. You can modify organisations, categories, or change the AND/OR logic.
                      </p>
                    </div>
                  </div>
                ) : (
                  <svg
                    onDrag={(e) => e.preventDefault()}
                    ref={svgRef}
                    className='cursor-grab bg-transparent'
                  ></svg>
                )}
              </div>
            )}
          </>
        ) : (
          <div className='grid h-[calc(100vh-10rem)] w-full place-items-center'>
            {isLoadingCategories ? (
              <div className='text-gray-500'>Loading...</div>
            ) : (
              <div className='text-gray-500'>
                Select a category on the top left to get started
              </div>
            )}
          </div>
        )}

        <OrganisationDetails
          selectedOrganisation={selectedOrganisation || ''}
          isShowOrganisationDetails={isShowOrganisationDetails}
          setIsShowOrganisationDetails={setIsShowOrganisationDetails}
        />

        <Sheet key="filter-sheet" open={isShowFilter} onOpenChange={handleFilterSheetChange}>
          <SheetTrigger className='sr-only'>Open</SheetTrigger>
          <SheetContent className='w-full md:max-w-2xl flex flex-col'>
            <SheetHeader className='flex-shrink-0 pb-4 border-b mb-6'>
              <SheetTitle>Filter Map</SheetTitle>
            </SheetHeader>

            <div className='flex-1 overflow-y-auto space-y-8 pr-2 -mr-2 pb-6'>
              {/* Organisations Filter - First */}
              <div>
                <SidebarFilter
                  data={mapData}
                  filterCategory="organisations"
                  onFilterChange={handleUncheckedOrganisations}
                  uncheckedItems={uncheckedOrganisations}
                />
              </div>

              {/* AND/OR Toggle - Only show when both categories are selected */}
              {selectedCategory1.id && selectedCategory2.id && (
                <div className='space-y-2'>
                  <div>
                    <label className='text-base font-bold text-gray-900 mb-1 block'>
                      Filter Logic
                    </label>
                    <p className='text-sm text-gray-500 mb-3'>
                      Choose how categories are combined when filtering organisations
                    </p>
                  </div>
                  <ToggleGroup
                    type='single'
                    value={filterLogic}
                    onValueChange={(value) => {
                      if (value === 'AND' || value === 'OR') {
                        setFilterLogic(value);
                      }
                    }}
                    className='inline-flex h-10 items-center justify-start rounded-lg bg-gray-200 p-0.5 w-fit gap-0'
                  >
                    <ToggleGroupItem
                      value='OR'
                      aria-label='OR logic'
                      className={cn(
                        'px-8 py-2 text-sm font-semibold rounded-md transition-all border-0 shadow-none uppercase',
                        'data-[state=on]:!bg-[#2563eb] data-[state=on]:!text-white',
                        filterLogic === 'OR'
                          ? '!bg-[#2563eb] !text-white'
                          : 'bg-transparent text-gray-700'
                      )}
                    >
                      OR
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value='AND'
                      aria-label='AND logic'
                      className={cn(
                        'px-8 py-2 text-sm font-semibold rounded-md transition-all border-0 shadow-none uppercase',
                        'data-[state=on]:!bg-[#2563eb] data-[state=on]:!text-white',
                        filterLogic === 'AND'
                          ? '!bg-[#2563eb] !text-white'
                          : 'bg-transparent text-gray-700'
                      )}
                    >
                      AND
                    </ToggleGroupItem>
                  </ToggleGroup>
                  <div className='text-[10px] text-gray-500 mt-2 space-y-0.5'>
                    <p>
                      <span className='font-medium text-gray-700'>OR:</span> Show organisations matching tags in either category
                    </p>
                    <p>
                      <span className='font-medium text-gray-700'>AND:</span> Show organisations matching tags in both categories
                    </p>
                  </div>
                </div>
              )}

              {/* Category 1 Filter */}
              {selectedCategory1.id && (
                <div>
                  <SidebarFilter
                    data={mapData}
                    filterCategory={{id: selectedCategory1.id, name: selectedCategory1.name}}
                    onFilterChange={handleUncheckedCategories1}
                    uncheckedItems={uncheckedCategories1}
                  />
                </div>
              )}

              {/* Category 2 Filter */}
              {selectedCategory2.id && (
                <div>
                  <SidebarFilter
                    data={mapData}
                    filterCategory={{id: selectedCategory2.id, name: selectedCategory2.name}}
                    onFilterChange={handleUncheckedCategories2}
                    uncheckedItems={uncheckedCategories2}
                  />
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>

        {(selectedCategory1.id || selectedCategory2.id) && (
          <div className='absolute bottom-10 right-4 mr-10 mt-5 flex h-fit gap-2 rounded-md bg-white p-2'>
            <div>
              <Button
                className='aspect-square bg-transparent p-1 text-black'
                title='Zoom In'
                onMouseDown={startZoomIn}
                onMouseUp={stopZoomIn}
                onMouseLeave={stopZoomIn}
              >
                <ZoomIn className='h-5 w-5' />
              </Button>
              <Button
                className='aspect-square bg-transparent p-1 text-black'
                title='Zoom Out'
                onMouseDown={startZoomOut}
                onMouseUp={stopZoomOut}
                onMouseLeave={stopZoomOut}
              >
                <ZoomOut className='h-5 w-5' />
              </Button>
              <Button
                className='aspect-square bg-transparent p-1 text-black'
                title='Reset'
                onClick={handleResetZoom}
              >
                <ResetIcon className='h-5 w-5' />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StakeHolderMap;
